import { motion, AnimatePresence } from 'framer-motion';
import { X, Info } from 'lucide-react';
import type { Track } from '@vtak/shared';
import { useDetailModalStore } from '@/stores/detail-modal-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { getMarker, type MarkerData } from '@/map/markers/marker-state';
import { lucideIconFor } from '@/map/lucide-marker-icons';
import { resolveEntityDetail, type EntityDetail, type DetailSection } from '@/map/detail/entity-detail';
import { SATELLITE_LAYER } from '@/map/coverage/coverage-frame';
import { useSatelliteProfile, type SatelliteProfile } from '@/hooks/use-satellite-profile';

/** Rich detail modal for the selected entity (satellite/aircraft/vessel/other). */
export function EntityDetailModal(): React.JSX.Element {
  const openUid = useDetailModalStore((s) => s.openUid);
  const close = useDetailModalStore((s) => s.close);
  const dataMap = usePluginDataStore((s) => s.data);
  const marker = openUid ? getMarker(openUid) : undefined;
  const track = marker ? findTrack(dataMap, marker.layer, marker.uid) : undefined;
  const profile = useSatelliteProfile(satelliteNorad(marker, track));

  return (
    <AnimatePresence>
      {marker && (
        <Modal
          key={marker.uid}
          marker={marker}
          detail={mergeProfile(resolveEntityDetail(marker, track), profile)}
          onClose={close}
        />
      )}
    </AnimatePresence>
  );
}

/** NORAD id of the selected entity when it is a satellite, else undefined. */
function satelliteNorad(marker: MarkerData | undefined, track: Track | undefined): number | undefined {
  if (!marker || marker.layer !== SATELLITE_LAYER) return undefined;
  const id = Number(track?.props['noradId']);
  return Number.isFinite(id) ? id : undefined;
}

/** Merge a real SatNOGS profile into the detail: image becomes the hero, plus a Profile section. */
function mergeProfile(detail: EntityDetail, profile: SatelliteProfile | null): EntityDetail {
  if (!profile) return detail;
  const rows = [
    { label: 'Operator', value: profile.operator },
    { label: 'Status', value: titleCase(profile.status) },
    { label: 'Launched', value: formatDate(profile.launched) },
    { label: 'Country', value: profile.country },
  ].filter((row) => row.value.length > 0);
  return {
    ...detail,
    imageUrl: profile.image || detail.imageUrl,
    sections: rows.length > 0 ? [...detail.sections, { title: 'Profile', rows }] : detail.sections,
  };
}

/** Capitalize the first letter, or blank. */
function titleCase(value: string): string {
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}

/** Format an ISO date to a short local date, or blank. */
function formatDate(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Find the source track for a selected uid within its layer. */
function findTrack(dataMap: ReadonlyMap<string, unknown[]>, layer: string, uid: string): Track | undefined {
  const list = dataMap.get(layer) as Track[] | undefined;
  return list?.find((track) => track.uid === uid);
}

/** Animated modal body. */
function Modal({
  marker,
  detail,
  onClose,
}: {
  marker: MarkerData;
  detail: EntityDetail;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <div className="absolute inset-0 z-[1100] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-echo-border bg-echo-grey shadow-2xl ring-1 ring-black/30"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-echo-border px-4 py-3">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: marker.color }} />
          <h2 className="flex-1 truncate font-chakra text-sm font-semibold uppercase tracking-wider text-foreground">
            {marker.callsign}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <Hero detail={detail} marker={marker} />
          {detail.sections.map((section) => (
            <Section key={section.title} section={section} />
          ))}
          <Footnotes detail={detail} />
        </div>
      </motion.div>
    </div>
  );
}

/** Hero image (real photo) or a large affiliation-tinted glyph from the layer icon. */
function Hero({ detail, marker }: { detail: EntityDetail; marker: MarkerData }): React.JSX.Element {
  if (detail.imageUrl) {
    return <img src={detail.imageUrl} alt="" className="max-h-48 w-full object-cover" />;
  }
  const Icon = lucideIconFor(marker.icon);
  if (!Icon) return <div className="h-2" />;
  return (
    <div className="flex h-32 items-center justify-center bg-gradient-to-b from-white/[0.06] to-transparent">
      <Icon className="h-16 w-16" style={{ color: marker.color }} strokeWidth={1.5} />
    </div>
  );
}

/** A titled section of label/value rows (empty rows hidden). */
function Section({ section }: { section: DetailSection }): React.JSX.Element | null {
  const rows = section.rows.filter((row) => row.value.length > 0);
  if (rows.length === 0) return null;
  return (
    <div className="border-t border-echo-border px-4 py-3">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-echo-yellow">{section.title}</div>
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{row.label}</span>
            <span className="truncate text-right text-sm text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Attribution + follow-up notes at the modal foot. */
function Footnotes({ detail }: { detail: EntityDetail }): React.JSX.Element | null {
  const notes = detail.followUps ?? [];
  if (notes.length === 0 && !detail.attribution) return null;
  return (
    <div className="border-t border-echo-border px-4 py-3">
      {notes.map((note) => (
        <div key={note} className="mb-1 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{note}</span>
        </div>
      ))}
      {detail.attribution && <div className="text-[10px] text-muted-foreground/70">{detail.attribution}</div>}
    </div>
  );
}
